import axios from 'axios';
import { _authHeader } from './authHeader';

/**
 * Service for recording contract field interactions to the client-facing app.
 * Uses a separate base URL (the booking/client app) but the same JWT token.
 * Interactions are fire-and-forget — failures are logged but don't block the UI.
 */
const http = axios.create({
  baseURL: process.env.VUE_APP_PORTAL_URL,
  headers: _authHeader(),
});

// Keep auth header fresh on each request
http.interceptors.request.use((config) => {
  const headers = _authHeader();
  if (headers.Authorization) {
    config.headers.Authorization = headers.Authorization;
  } else {
    console.warn('[ContractInteraction] No auth token available for request');
  }
  return config;
});

/**
 * Compute SHA-256 hash of the given input.
 * Uses SubtleCrypto when available, falls back to pure-JS implementation.
 *
 * @param {ArrayBuffer|Uint8Array|string} input
 * @returns {Promise<string>} Hex-encoded SHA-256 hash.
 */
async function computeSha256(input) {
  let data;
  if (typeof input === 'string') {
    data = new TextEncoder().encode(input);
  } else if (input instanceof ArrayBuffer) {
    data = new Uint8Array(input);
  } else {
    data = input;
  }

  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  return sha256Fallback(data);
}

/**
 * Pure-JS SHA-256 fallback for non-secure contexts.
 *
 * @param {Uint8Array} msgBytes
 * @returns {string}
 */
function sha256Fallback(msgBytes) {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const rotr = (n, x) => (x >>> n) | (x << (32 - n));
  const ch = (x, y, z) => (x & y) ^ (~x & z);
  const maj = (x, y, z) => (x & y) ^ (x & z) ^ (y & z);
  const sigma0 = (x) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
  const sigma1 = (x) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
  const gamma0 = (x) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
  const gamma1 = (x) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);

  const msgLen = msgBytes.length;
  const bitLen = msgLen * 8;
  const padded = new Uint8Array(Math.ceil((msgLen + 9) / 64) * 64);
  padded.set(msgBytes);
  padded[msgLen] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 4, bitLen, false);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  for (let offset = 0; offset < padded.length; offset += 64) {
    const w = new Array(64);
    for (let i = 0; i < 16; i++) {
      w[i] = view.getUint32(offset + i * 4, false);
    }
    for (let i = 16; i < 64; i++) {
      w[i] = (gamma1(w[i - 2]) + w[i - 7] + gamma0(w[i - 15]) + w[i - 16]) | 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i++) {
      const t1 = (h + sigma1(e) + ch(e, f, g) + K[i] + w[i]) | 0;
      const t2 = (sigma0(a) + maj(a, b, c)) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }

    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const hex = (n) => (n >>> 0).toString(16).padStart(8, '0');
  return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7);
}

export default new class ContractInteractionService {
  constructor() {
    this._documentHash = null;
    this._hashPromise = null;
    this._hashSource = null;
  }

  /**
   * Set the document hash directly from a server-provided value.
   * Preferred over local computation to avoid mismatches.
   *
   * @param {string} hash - Pre-computed SHA-256 hex string from VC API.
   */
  setDocumentHash(hash) {
    this._documentHash = hash;
    this._hashPromise = null;
    this._hashSource = 'server';
    console.log('[ContractInteraction] Hash set from server:', hash);
  }

  /**
   * Compute and cache the document hash from PDF bytes.
   * Fallback for when server hash is not available (e.g. new upload before save).
   *
   * @param {ArrayBuffer|Uint8Array} pdfBytes
   * @returns {Promise<string>}
   */
  async setDocumentBytes(pdfBytes) {
    const bytes = pdfBytes instanceof ArrayBuffer ? new Uint8Array(pdfBytes) : pdfBytes;
    this._hashPromise = computeSha256(bytes);
    this._documentHash = await this._hashPromise;
    this._hashPromise = null;
    this._hashSource = 'computed';
    console.log('[ContractInteraction] Hash computed from bytes:', this._documentHash, '| byteLength:', bytes.byteLength);
    return this._documentHash;
  }

  /**
   * Get the cached document hash, waiting if computation is in progress.
   *
   * @returns {Promise<string|null>}
   */
  async getDocumentHash() {
    if (this._documentHash) return this._documentHash;
    if (this._hashPromise) return this._hashPromise;
    return null;
  }

  /**
   * Synchronously return the cached hash (or null if not yet available).
   *
   * @returns {string|null}
   */
  getDocumentHashSync() {
    return this._documentHash;
  }

  /**
   * Reset cached state (call when wizard closes).
   */
  reset() {
    this._documentHash = null;
    this._hashPromise = null;
    this._hashSource = null;
  }

  /**
   * Record a field interaction (fire-and-forget).
   *
   * @param {number|string} eventId   - The lead event ID.
   * @param {number|string} venueId   - The venue ID.
   * @param {Object}        payload   - Interaction data.
   * @param {number}        payload.document_id
   * @param {string}        payload.field_id
   * @param {string}        payload.field_type       - 'initials', 'signature', 'date', 'text'
   * @param {string}        payload.interaction_type - 'focused_on_field' or 'value_updated'
   * @param {string|null}   payload.field_value
   * @param {string|null}   payload.custom_label
   */
  async recordInteraction(eventId, venueId, payload) {
    try {
      const documentHash = await this.getDocumentHash();
      if (!documentHash) {
        console.warn('[ContractInteraction] Document hash not available, skipping interaction.');
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user && (user.id || (user.data ? user.data.id : null));

      console.log('[ContractInteraction] Sending hash:', documentHash, '| source:', this._hashSource || 'computed');

      await http.post(`/api/event/${eventId}/venue/${venueId}/contract/signing/interactions`, {
        document_id: payload.document_id,
        field_id: payload.field_id,
        field_type: payload.field_type,
        interaction_type: payload.interaction_type,
        field_value: payload.field_value || null,
        custom_label: payload.custom_label || null,
        executing_party: 'venuecrew',
        client_timestamp: new Date().toISOString(),
        document_hash: documentHash,
        user_id: userId,
      });
    } catch (error) {
      // Fire-and-forget: log but don't throw
      console.error('[ContractInteraction] Failed to record interaction:', error.message || error);
    }
  }

  /**
   * Compute SHA-256 of a data URL or string value (for signature/initials tracking).
   *
   * @param {string} value
   * @returns {Promise<string>}
   */
  async hashValue(value) {
    return computeSha256(value);
  }
}();
